import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LegalFooter } from "@/components/LegalFooter";
import { BookOpen, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopEbooksPage() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/ebooks/public/categories?lang=${lang}`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    const catQ = activeCat && activeCat !== "all" ? `&category=${activeCat}` : "";
    axios.get(`${BACKEND_URL}/api/ebooks/public?lang=${lang}${catQ}`)
      .then((r) => setItems(r.data.ebooks || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [lang, activeCat]);

  const totalCount = useMemo(
    () => categories.reduce((acc, c) => acc + (c.count || 0), 0),
    [categories]
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="shop-ebooks-page">
      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <Link to="/shop" className="font-mono text-[10px] uppercase text-white/40 hover:text-white">
          ← EMRKT.CH
        </Link>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display font-light text-3xl sm:text-5xl tracking-tight">
              {t(lang, "ebooks_title")}
            </h1>
            <p className="mt-3 text-white/60 text-sm max-w-2xl">
              {t(lang, "ebooks_subtitle")}
            </p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {items.length} {t(lang, "ebooks_available")}
          </div>
        </div>

        {categories.length > 0 && (
          <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="ebooks-category-bar">
            <EbookCatChip slug="all" label={t(lang, "shop_cat_all") || "All"} count={totalCount}
                          active={activeCat === "all"} onClick={() => setActiveCat("all")} />
            {categories.map((c) => (
              <EbookCatChip key={c.slug} slug={c.slug} label={c.label} count={c.count}
                            active={activeCat === c.slug} onClick={() => setActiveCat(c.slug)} />
            ))}
          </div>
        )}
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <div className="text-center py-20 text-white/40 text-sm">…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-white/40 text-sm">
            {t(lang, "ebooks_none_yet")}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((e) => <EbookCard key={e.slug} ebook={e} />)}
          </div>
        )}
      </section>

      <LegalFooter />
    </div>
  );
}

function EbookCatChip({ slug, label, count, active, onClick }) {
  return (
    <button
      data-testid={`ebook-cat-${slug}`}
      onClick={onClick}
      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-all ${
        active
          ? "bg-white text-black border-white"
          : "text-white/70 border-white/15 hover:border-white/50"
      }`}
    >
      {label} <span className="opacity-60 ml-1">· {count}</span>
    </button>
  );
}

function EbookCard({ ebook }) {
  return (
    <Link
      to={`/shop/ebooks/${ebook.slug}`}
      data-testid={`ebook-card-${ebook.slug}`}
      className="group block rounded-lg border border-white/10 overflow-hidden hover:border-[color:var(--hq-cyan)]/50 transition-colors"
    >
      <div
        className="aspect-[3/4] p-6 flex flex-col justify-between relative"
        style={{
          background: `linear-gradient(135deg, ${ebook.cover_hex || "#0E7C86"} 0%, #050505 130%)`,
        }}
      >
        <div className="flex items-center justify-between">
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/70">
            EMRKT.CH Guide
          </div>
          {ebook.available_langs && ebook.available_langs.length > 1 && (
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/70 bg-black/30 rounded px-1.5 py-0.5">
              {ebook.available_langs.join(" · ").toUpperCase()}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-white font-display text-xl leading-tight">
            {ebook.title}
          </div>
          {ebook.subtitle && (
            <div className="text-white/80 text-xs italic line-clamp-2">
              {ebook.subtitle}
            </div>
          )}
        </div>
        <div className="text-white/60 text-[10px] font-mono uppercase">
          {ebook.author_persona}
        </div>
      </div>
      <div className="p-4 bg-black/60 border-t border-white/10 flex items-center justify-between">
        <div className="flex gap-3 text-[10px] text-white/50 font-mono uppercase">
          {ebook.reading_minutes && (
            <span className="flex items-center gap-1"><Clock size={10} /> {ebook.reading_minutes}m</span>
          )}
          <span className="flex items-center gap-1"><BookOpen size={10} /> {ebook.chapter_count}</span>
        </div>
        <div className="text-white font-mono text-sm tracking-wide">
          CHF {(ebook.price_chf ?? 0).toFixed(2)}
        </div>
      </div>
    </Link>
  );
}
